import React, { useState, useEffect } from "react";
import {
  collection, getDocs, orderBy, query,
  doc, updateDoc, getDoc, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { SHOP } from "../shop";

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatDate = (value) => {
  if (!value) return "—";
  if (typeof value.toDate === "function") {
    return value.toDate().toLocaleDateString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  }
  if (typeof value === "string" && value.length >= 10) {
    const [y, m, d] = value.split("-");
    return `${d}.${m}.${y}`;
  }
  return String(value);
};

const Stars = ({ rating }) => {
  const n = Math.max(0, Math.min(5, Number(rating) || 0));
  return (
    <span style={{ color: "#d69e2e", fontSize: "1.1rem", letterSpacing: "2px" }}>
      {"★".repeat(n)}{"☆".repeat(5 - n)}
    </span>
  );
};

// ── Status badge ──────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  pending:  { background: "#fefcbf", color: "#744210", border: "1px solid #f6e05e" },
  approved: { background: "#c6f6d5", color: "#22543d", border: "1px solid #68d391" },
  posted:   { background: "#e2e8f0", color: "#4a5568", border: "1px solid #cbd5e0" },
  skipped:  { background: "#f7fafc", color: "#a0aec0", border: "1px solid #e2e8f0" },
  error:    { background: "#fff5f5", color: "#c53030", border: "1px solid #fc8181" },
};
const STATUS_LABELS = {
  pending:  "Offen",
  approved: "Freigegeben",
  posted:   "Gepostet",
  skipped:  "Übersprungen",
  error:    "Fehler",
};

const StatusBadge = ({ status }) => (
  <span style={{
    ...(STATUS_STYLES[status] || STATUS_STYLES.pending),
    padding: "0.2em 0.65em", borderRadius: "12px",
    fontSize: "0.78rem", fontWeight: "600", whiteSpace: "nowrap",
  }}>
    {STATUS_LABELS[status] || status}
  </span>
);

// ── Review card ───────────────────────────────────────────────────────────────

const ReviewCard = ({ review, onUpdate }) => {
  const [replyText, setReplyText]   = useState(review.final_reply || review.ai_reply || "");
  const [saving, setSaving]         = useState(false);
  const [expanded, setExpanded]     = useState(review.status === "pending");
  const [regenerating, setRegenerating] = useState(false);

  const isDone = review.status === "approved" || review.status === "posted";

  const handleApprove = async () => {
    if (!replyText.trim()) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, "etsy_reviews", review.id), {
        final_reply:  replyText.trim(),
        status:       "approved",
        approved_at:  serverTimestamp(),
      });
      onUpdate(review.id, { ...review, final_reply: replyText.trim(), status: "approved" });
      setExpanded(false);
    } catch (err) {
      alert("Fehler beim Speichern: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = async () => {
    setSaving(true);
    try {
      await updateDoc(doc(db, "etsy_reviews", review.id), { status: "skipped" });
      onUpdate(review.id, { ...review, status: "skipped" });
      setExpanded(false);
    } catch (err) {
      alert("Fehler: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      await updateDoc(doc(db, "etsy_reviews", review.id), {
        regenerateRequested: true,
      });
      // Poll until scraper clears the flag and writes new ai_reply (~10s)
      const poll = setInterval(async () => {
        const snap = await getDoc(doc(db, "etsy_reviews", review.id));
        if (!snap.exists()) { clearInterval(poll); setRegenerating(false); return; }
        const data = snap.data();
        if (!data.regenerateRequested) {
          clearInterval(poll);
          setReplyText(data.ai_reply || "");
          setRegenerating(false);
        }
      }, 4000);
      // Give up after 90s if scraper isn't running
      setTimeout(() => {
        clearInterval(poll);
        setRegenerating(false);
      }, 90000);
    } catch (err) {
      alert("Fehler beim Generieren: " + err.message);
      setRegenerating(false);
    }
  };

  return (
    <div style={{
      background: "#fff", borderRadius: "10px",
      boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
      marginBottom: "1em",
      borderLeft: `4px solid ${
        review.status === "approved" ? "#68d391" :
        review.status === "posted"   ? "#cbd5e0" :
        review.status === "skipped"  ? "#e2e8f0" :
        review.status === "error"    ? "#fc8181" : "#f6e05e"
      }`,
    }}>
      {/* Card header — always visible */}
      <div
        onClick={() => setExpanded((v) => !v)}
        style={{
          display: "flex", alignItems: "center",
          gap: "0.75em", padding: "1em 1.25em",
          cursor: "pointer", flexWrap: "wrap",
        }}
      >
        <Stars rating={review.rating} />
        <span style={{ fontWeight: "600", color: "#2d3748", fontSize: "0.9rem" }}>
          {review.reviewer_name || "Unbekannt"}
        </span>
        {review.product_name && (
          <span style={{ color: "#718096", fontSize: "0.8rem", flex: 1,
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            · {review.product_name}
          </span>
        )}
        <span style={{ color: "#a0aec0", fontSize: "0.78rem", whiteSpace: "nowrap" }}>
          {formatDate(review.review_date)}
        </span>
        <StatusBadge status={review.status} />
        <span style={{ color: "#a0aec0", fontSize: "0.75rem", marginLeft: "auto" }}>
          {expanded ? "▴" : "▾"}
        </span>
      </div>

      {/* Expanded body */}
      {expanded && (
        <div style={{ padding: "0 1.25em 1.25em" }}>
          {/* Product name */}
          {review.product_name && (
            <div style={{
              fontSize: "0.8rem", color: "#718096",
              marginBottom: "0.6em",
            }}>
              <span style={{ fontWeight: "600", color: "#4a5568" }}>Produkt: </span>
              {review.product_name}
            </div>
          )}

          {/* Review text */}
          {review.review_text ? (
            <div style={{
              background: "#fdf7f4", border: "1px solid #e8d8d0",
              borderRadius: "6px", padding: "0.75em 1em",
              fontSize: "0.875rem", color: "#2d3748",
              lineHeight: "1.6", marginBottom: "1em",
              fontStyle: "italic", whiteSpace: "pre-wrap",
            }}>
              „{review.review_text}"
            </div>
          ) : (
            <p style={{ color: "#a0aec0", fontSize: "0.82rem",
              fontStyle: "italic", marginBottom: "1em" }}>
              Kein Rezensionstext.
            </p>
          )}

          {/* Reply draft */}
          <label style={{
            display: "block", fontWeight: "600", fontSize: "0.8rem",
            color: "#4a5568", marginBottom: "0.4em",
            textTransform: "uppercase", letterSpacing: "0.04em",
          }}>
            {isDone ? "Antwort" : "KI-Entwurf (bearbeitbar)"}
          </label>
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            readOnly={isDone}
            rows={4}
            style={{
              width: "100%", boxSizing: "border-box",
              padding: "0.65em 0.85em",
              border: `1px solid ${isDone ? "#e2e8f0" : "#c8a882"}`,
              borderRadius: "6px", fontSize: "0.875rem",
              color: isDone ? "#718096" : "#2d3748",
              background: isDone ? "#f7fafc" : "#fffdf9",
              resize: "vertical", fontFamily: "inherit",
              lineHeight: "1.5",
            }}
          />

          {/* Actions — for pending or error */}
          {(review.status === "pending" || review.status === "error") && (
            review.can_reply === false ? (
              <div style={{ marginTop: "0.85em" }}>
                <span style={{ color: "#a0aec0", fontSize: "0.8rem" }}>
                  Antwort nicht verfügbar (Etsy)
                </span>
              </div>
            ) : (
              <div style={{ display: "flex", gap: "0.75em", marginTop: "0.85em", flexWrap: "wrap" }}>
                <button
                  onClick={handleApprove}
                  disabled={saving || regenerating || !replyText.trim()}
                  style={{
                    padding: "0.5em 1.4em", borderRadius: "6px", border: "none",
                    background: (saving || regenerating) ? "#a0aec0" : "#276749",
                    color: "#fff", fontWeight: "700", fontSize: "0.875rem",
                    cursor: (saving || regenerating) ? "not-allowed" : "pointer",
                  }}
                >
                  {saving ? "Wird gespeichert…" : "Freigeben ✓"}
                </button>
                {review.status === "pending" && (
                  <button
                    onClick={handleSkip}
                    disabled={saving || regenerating}
                    style={{
                      padding: "0.5em 1.1em", borderRadius: "6px",
                      border: "1px solid #cbd5e0", background: "#fff",
                      color: "#718096", fontWeight: "600", fontSize: "0.875rem",
                      cursor: (saving || regenerating) ? "not-allowed" : "pointer",
                    }}
                  >
                    Überspringen
                  </button>
                )}
                <button
                  onClick={handleRegenerate}
                  disabled={saving || regenerating}
                  style={{
                    padding: "0.5em 1.1em", borderRadius: "6px",
                    border: "1px solid #b794f4", background: "#faf5ff",
                    color: "#553c9a", fontWeight: "600", fontSize: "0.875rem",
                    cursor: (saving || regenerating) ? "not-allowed" : "pointer",
                    opacity: (saving || regenerating) ? 0.6 : 1,
                  }}
                >
                  {regenerating ? "Wird generiert…" : "↺ Neu generieren"}
                </button>
              </div>
            )
          )}

          {/* Status info messages */}
          {review.status === "approved" && (
            <p style={{ marginTop: "0.75em", fontSize: "0.8rem", color: "#276749" }}>
              ✓ Freigegeben — wird automatisch auf Etsy gepostet
            </p>
          )}
          {review.status === "posted" && (
            <p style={{ marginTop: "0.75em", fontSize: "0.8rem", color: "#718096" }}>
              ✓ Auf Etsy gepostet am {formatDate(review.posted_at)}
            </p>
          )}
          {review.status === "error" && (
            <p style={{ marginTop: "0.75em", fontSize: "0.8rem", color: "#c53030" }}>
              ✗ Posting fehlgeschlagen
              {review.apiError ? `: ${review.apiError}` : ""}
              {" — Antwort erneut freigeben um es nochmal zu versuchen."}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const ReviewsPage = () => {
  const [reviews, setReviews]   = useState([]);
  const [loading, setLoading]   = useState(true);
  const [filter, setFilter]     = useState("pending");

  useEffect(() => {
    getDocs(query(collection(db, "etsy_reviews"), orderBy("review_date", "desc")))
      .then((snap) => {
        setReviews(snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((r) => !r.shop_name || r.shop_name.toLowerCase() === SHOP.name.toLowerCase())
        );
      })
      .catch((err) => console.error("Failed to load reviews:", err))
      .finally(() => setLoading(false));
  }, []);

  const handleUpdate = (id, updated) => {
    setReviews((prev) => prev.map((r) => (r.id === id ? updated : r)));
  };

  const counts = {
    pending:  reviews.filter((r) => r.status === "pending").length,
    approved: reviews.filter((r) => r.status === "approved").length,
    posted:   reviews.filter((r) => r.status === "posted").length,
    error:    reviews.filter((r) => r.status === "error").length,
    all:      reviews.length,
  };

  const FILTERS = [
    { key: "pending",  label: "Offen" },
    { key: "approved", label: "Freigegeben" },
    { key: "posted",   label: "Gepostet" },
    ...(counts.error > 0 ? [{ key: "error", label: "Fehler" }] : []),
    { key: "all",      label: "Alle" },
  ];

  const filtered = filter === "all"
    ? reviews
    : reviews.filter((r) => r.status === filter);

  const toggleStyle = (key) => ({
    padding: "0.4em 1.1em", borderRadius: "6px",
    border: `1px solid ${filter === key ? "#4a2c2a" : "#cbd5e0"}`,
    background: filter === key ? "#4a2c2a" : "#fff",
    color: filter === key ? "#fff" : "#4a5568",
    fontWeight: "600", fontSize: "0.85rem", cursor: "pointer",
  });

  return (
    <div style={{ maxWidth: 800, margin: "0 auto" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: "1em", marginBottom: "0.75em" }}>
        <h2 style={{ margin: 0, fontSize: "1.25rem", color: "#1a202c" }}>
          Bewertungen & Antworten
        </h2>
        <a
          href={`https://www.etsy.com/shop/${SHOP.etsyShop}/reviews`}
          target="_blank"
          rel="noreferrer"
          style={{
            padding: "0.25em 0.75em", borderRadius: "6px",
            border: "1px solid #cbd5e0", background: "#fff",
            color: "#4a5568", fontSize: "0.8rem", fontWeight: "500",
            textDecoration: "none", whiteSpace: "nowrap",
          }}
        >
          ETSY Reviews ↗
        </a>
      </div>

      {/* Filter pills */}
      <div style={{ display: "flex", gap: "0.5em", flexWrap: "wrap", marginBottom: "1.25em" }}>
        {FILTERS.map(({ key, label }) => (
          <button key={key} style={toggleStyle(key)} onClick={() => setFilter(key)}>
            {label} ({counts[key] ?? reviews.filter((r) => r.status === key).length})
          </button>
        ))}
      </div>

      {/* Info banner */}
      <div style={{
        background: "#fffbeb", border: "1px solid #f6e05e",
        borderRadius: "8px", padding: "0.65em 1em",
        marginBottom: "1.25em", fontSize: "0.8rem", color: "#744210",
      }}>
        Neue Bewertungen werden alle 30 Minuten per API erkannt.
        KI-Entwürfe werden automatisch generiert.
        Freigegebene Antworten werden automatisch auf Etsy gepostet.
      </div>

      {/* Content */}
      {loading ? (
        <p style={{ color: "#718096", fontStyle: "italic" }}>Lade Bewertungen…</p>
      ) : filtered.length === 0 ? (
        <div style={{
          background: "#fff", borderRadius: "10px",
          boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
          padding: "3em", textAlign: "center", color: "#718096",
        }}>
          <p style={{ margin: 0 }}>
            {filter === "pending"
              ? "Keine offenen Bewertungen."
              : filter === "approved"
                ? "Noch keine freigegebenen Antworten."
                : filter === "posted"
                  ? "Noch keine geposteten Antworten."
                  : filter === "error"
                    ? "Keine fehlgeschlagenen Postings."
                    : "Noch keine Bewertungen."}
          </p>
        </div>
      ) : (
        filtered.map((review) => (
          <ReviewCard key={review.id} review={review} onUpdate={handleUpdate} />
        ))
      )}

      {reviews.length > 0 && (
        <p style={{ marginTop: "1em", color: "#a0aec0",
          fontSize: "0.8rem", textAlign: "right" }}>
          {filtered.length} von {reviews.length} Bewertung{reviews.length !== 1 ? "en" : ""}
        </p>
      )}
    </div>
  );
};

export default ReviewsPage;
